import React, { useContext, useState, useEffect } from "react";
import { Button, Input, message, Modal, Form, Space, Tabs } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import Editor from "@monaco-editor/react";
import { PostJutgePracticaIdUsuariId } from "../../../../dao/jutge/PostJutgePracticaIdUsuariId";
import { PostJutgePracticaIdUsuariIdMulti } from "../../../../dao/jutge/PostJutgePracticaIdUsuariIdMulti";
import { AuthContext } from "../../../../config/AuthContext";

interface CodeFile {
    name: string;
    content: string;
}

interface CodeModalProps {
    open: boolean;
    onClose: () => void;
    id_practica: string;
    record: {
        id_usuari: string;
        alumne: string;
    } | null;
    initialCode?: string; // Legacy support
    files?: CodeFile[];   // New multi-file support
    applyHighlights?: (editor: any, monaco: any) => void;
    onCodeSent?: () => void;
}

const CodiFontModal: React.FC<CodeModalProps> = ({
    open,
    onClose,
    id_practica,
    record,
    initialCode,
    files,
    applyHighlights,
    onCodeSent,
}) => {
    // Merge files and initialCode into a unified structure
    const [fileList, setFileList] = useState<CodeFile[]>([]);
    const [activeKey, setActiveKey] = useState<string>("0");

    // State for the CURRENTLY active file (for editing)
    const [code, setCode] = useState("");
    const [filename, setFilename] = useState("");

    const [loading, setLoading] = useState(false);
    const [compiling, setCompiling] = useState(false);
    const [compileOutput, setCompileOutput] = useState<string | null>(null);
    const [userChangedFilename, setUserChangedFilename] = useState(false);
    const [isAddModalOpen, setIsAddModalOpen] = useState(false);
    const [newFileName, setNewFileName] = useState("");
    const { user } = useContext(AuthContext);

    const isEditable = user.tipus === "0" || user.tipus === "1";

    const getFileName = (path: string) => {
        if (!path) return "";
        return path.split(/[/\\]/).pop() || path;
    };

    useEffect(() => {
        if (open) {
            let initialFiles: CodeFile[] = [];
            if (files && files.length > 0) {
                initialFiles = files.map(f => ({ ...f, name: getFileName(f.name) }));
            } else if (initialCode) {
                // Try to detect filename from initialCode
                const match = initialCode.match(/\bclass\s+([A-Za-z_]\w*)/);
                const detectedName = (match && match[1]) ? `${match[1]}.java` : "Main.java";
                initialFiles = [{ name: detectedName, content: initialCode }];
            }

            setFileList(initialFiles);
            if (initialFiles.length > 0) {
                setActiveKey("0");
                setCode(initialFiles[0].content);
                setFilename(initialFiles[0].name);
            }
            setCompileOutput(null);
            setUserChangedFilename(false);
        }
    }, [initialCode, files, open]);

    // Handle Tab Change
    const onTabChange = (key: string) => {
        setActiveKey(key);
        const index = parseInt(key);
        if (fileList[index]) {
            setCode(fileList[index].content);
            setFilename(fileList[index].name);
            // Reset specific edit states if needed
            setUserChangedFilename(false);
        }
    };

    // Update fileList when code/filename changes (to keep state in sync if we switch back)
    // However, `code` state is what represents the editor content.
    // If we switch tabs, we lose unsaved edits in `fileList` unless we update it?
    // Let's simple update fileList on edit.
    const handleCodeChange = (value: string | undefined) => {
        const newCode = value || "";
        setCode(newCode);
        const index = parseInt(activeKey);
        setFileList(prev => {
            const next = [...prev];
            if (next[index]) next[index] = { ...next[index], content: newCode };
            return next;
        });
    };

    // Auto-update filename if not changed manually (Single file specific logic mostly)
    useEffect(() => {
        if (!isEditable || userChangedFilename || fileList.length > 1) return;
        // Only does auto-rename if single file to avoid confusion?
        // Or apply to current file?
        // Let's apply to current file if it seems safe.
        const match = code.match(/\bclass\s+([A-Za-z_]\w*)/);
        if (match && match[1]) {
            const className = match[1];
            const newName = `${className}.java`;
            setFilename(newName);
            setFileList(prev => {
                const index = parseInt(activeKey);
                const next = [...prev];
                if (next[index]) next[index] = { ...next[index], name: newName };
                return next;
            });
        }
    }, [code, isEditable, userChangedFilename, activeKey]);


    const handleSend = async () => {
        if (!record || !id_practica) return;
        if (fileList.length === 0) {
            message.warning("No hi ha fitxers per enviar.");
            return;
        }

        for (const file of fileList) {
            if (!file.name.trim().toLowerCase().endsWith(".java")) {
                message.error(`El nom del fitxer ${file.name} ha d'acabar amb .java`);
                return;
            }
        }

        setLoading(true);
        try {
            const uids: string[] = [];

            for (const file of fileList) {
                const blob = new Blob([file.content], { type: "text/plain" });
                const formData = new FormData();
                formData.append("file", blob, file.name);
                formData.append("uid", file.name);

                const uploadResponse = await fetch(
                    `${process.env.REACT_APP_API_URL}:8061/jutge/practica/${id_practica}/usuaris/${record.id_usuari}/upload`,
                    { method: "POST", body: formData }
                );

                if (!uploadResponse.ok) {
                    message.error(`Error al pujar el fitxer ${file.name}.`);
                    setLoading(false);
                    return;
                }
                
                uids.push(file.name);
            }

            const postResponse = await PostJutgePracticaIdUsuariIdMulti(
                id_practica + "",
                record.id_usuari,
                uids
            );

            if (postResponse?.response === "OK") {
                message.success("Tots els fitxers enviats i testejats correctament!");
                onClose();
                if (onCodeSent) await onCodeSent();
            } else {
                message.error(postResponse?.response || "Error en el test.");
            }
        } catch (err) {
            console.error(err);
            message.error("Error durant l'enviament.");
        }
        setLoading(false);
    };

    const handleCompile = async () => {
        // Collect all files for compilation
        // Piston api expects {name, content}
        const filesToCompile = fileList.map(f => ({ name: f.name, content: f.content }));

        // If current filename differs from list (due to manual edit text input), use current?
        // Let's assume fileList is up to date thanks to handleCodeChange.

        setCompiling(true);
        setCompileOutput(null);

        try {
            const response = await fetch("https://emkc.org/api/v2/piston/execute", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    language: "java",
                    version: "15.0.2",
                    files: filesToCompile,
                }),
            });

            if (!response.ok) {
                message.error("Error durant la compilació.");
                setCompiling(false);
                return;
            }

            const data = await response.json();
            const output =
                data.run?.stdout ||
                data.run?.stderr ||
                data.compile?.stderr ||
                "No s'ha retornat cap missatge.";
            setCompileOutput(output);
        } catch (err) {
            console.error(err);
            setCompileOutput("Error al compilar el codi (connexió o API).");
        }

        setCompiling(false);
    };

    const handleAddFile = () => {
        if (!newFileName.trim()) return;
        let name = newFileName.trim();
        if (!name.toLowerCase().endsWith(".java")) name += ".java";

        if (fileList.find(f => f.name === name)) {
            message.error("Aquest fitxer ja existeix.");
            return;
        }

        const newFile = { name, content: "" };
        const newList = [...fileList, newFile];
        setFileList(newList);
        
        // Select the new file
        const newIndex = newList.length - 1;
        setActiveKey(String(newIndex));
        setCode("");
        setFilename(name);
        setUserChangedFilename(false);
        
        setIsAddModalOpen(false);
        setNewFileName("");
    };

    const isFilenameValid = filename.trim().toLowerCase().endsWith(".java");

    return (
        <Modal
            open={open}
            onCancel={onClose}
            width="80%"
            title={`Codi font ${record ? `- ${record.alumne}` : ""}`}
            footer={
                isEditable && (
                    <Space>
                        <Button onClick={handleCompile} loading={compiling}>
                            Compilar (Tots)
                        </Button>
                        <Button
                            type="primary" // Send current file ONLY? Or all?
                            // Currently handleSend acts on ONE file (the active one) and calls PostJutgePracticaIdUsuariId (Single).
                            // If we want to support multi-file submission editing from here, we need PostJutgePracticaIdUsuariIdMulti logic
                            // But usually teacher edits ONE file to fix a student's mistake.
                            // Let's keep sending SINGLE file for now, or update logic if strictly required.
                            loading={loading}
                            onClick={handleSend}
                        >
                            Enviar al jutge (Tots els fitxers)
                        </Button>
                    </Space>
                )
            }
        >
            <div style={{ marginBottom: 12, textAlign: 'right' }}>
                {isEditable && (
                    <Button 
                        type="primary" 
                        size="small" 
                        ghost 
                        icon={<PlusOutlined />} 
                        onClick={() => setIsAddModalOpen(true)}
                    >
                        Afegir Fitxer
                    </Button>
                )}
            </div>
            <Tabs
                activeKey={activeKey}
                onChange={onTabChange}
                items={fileList.map((file, i) => ({
                    label: file.name,
                    key: String(i),
                    children: (
                        <>
                            {isEditable && (
                                <Form layout="vertical" style={{ marginBottom: 12 }}>
                                    <Form.Item
                                        label="Nom del fitxer"
                                        required
                                        validateStatus={!isFilenameValid ? "error" : ""}
                                        help={!isFilenameValid ? "El nom del fitxer ha d'acabar amb .java" : ""}
                                    >
                                        <Input
                                            value={filename}
                                            onChange={(e) => {
                                                setFilename(e.target.value);
                                                setUserChangedFilename(true);
                                                setFileList(prev => {
                                                    const next = [...prev];
                                                    if (next[i]) next[i] = { ...next[i], name: e.target.value };
                                                    return next;
                                                });
                                            }}
                                        />
                                    </Form.Item>
                                </Form>
                            )}
                            <Editor
                                height="50vh"
                                defaultLanguage="java"
                                value={code} // This is controlled by state, updated on tab change
                                theme="light"
                                options={{
                                    readOnly: !isEditable,
                                    automaticLayout: true,
                                }}
                                onChange={handleCodeChange}
                                onMount={(editor, monaco) => {
                                    if (applyHighlights) applyHighlights(editor, monaco);
                                }}
                            />
                        </>
                    )
                }))}
            />

            {compileOutput && (
                <div
                    style={{
                        marginTop: 16,
                        padding: 12,
                        backgroundColor: "#f9f9f9",
                        border: "1px solid #ddd",
                        borderRadius: 8,
                        fontFamily: "monospace",
                        whiteSpace: "pre-wrap",
                    }}
                >
                    <strong>Resultat de la compilació:</strong>
                    <br />
                    {compileOutput}
                </div>
            )}

            <Modal
                title="Afegir nou fitxer"
                open={isAddModalOpen}
                onOk={handleAddFile}
                onCancel={() => { setIsAddModalOpen(false); setNewFileName(""); }}
                okText="Afegir"
                cancelText="Cancel·lar"
            >
                <div style={{ marginBottom: 10 }}>Nom del nou fitxer (.java):</div>
                <Input 
                    placeholder="NomFitxer.java" 
                    value={newFileName}
                    onChange={(e) => setNewFileName(e.target.value)}
                    onPressEnter={handleAddFile}
                    autoFocus
                />
            </Modal>
        </Modal>
    );
};

export default CodiFontModal;
